import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { taxReturnsAPI } from '../lib/taxReturnsAPI';
import { clientsAPI } from '../lib/clientsAPI';

export default function NewTaxReturn() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedTaxYear, setSelectedTaxYear] = useState('2024-25');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const response = await clientsAPI.list();
      setClients(response.data || []);
    } catch (error) {
      toast.error('Failed to load clients');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedClient) {
      toast.error('Please select a client');
      return;
    }

    setSubmitting(true);
    try {
      const response = await taxReturnsAPI.createReturn({
        client_id: selectedClient,
        tax_year: selectedTaxYear
      });
      
      toast.success('Tax return created successfully');
      navigate(`/tax-returns/${response.data.id}/questionnaire`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tax return');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-96 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}} data-testid="new-return-title">
            Start New Tax Return
          </h1>
          <p className="text-muted-foreground mt-2">Create an individual tax return for the selected tax year</p>
        </div>

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle>Tax Return Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="client">Client *</Label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger id="client" data-testid="client-select">
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tax_year">Tax Year *</Label>
              <Select value={selectedTaxYear} onValueChange={setSelectedTaxYear}>
                <SelectTrigger id="tax_year" data-testid="tax-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2024-25">2024-25</SelectItem>
                  <SelectItem value="2023-24">2023-24</SelectItem>
                  <SelectItem value="2022-23">2022-23</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="pt-4 flex gap-3">
              <Button
                onClick={() => navigate('/tax-returns')}
                variant="outline"
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                className="bg-primary hover:bg-primary/90"
                disabled={submitting || !selectedClient}
                data-testid="create-return-button"
              >
                {submitting ? 'Creating...' : (
                  <>
                    Create & Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}